import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import {
  financeLegacyService,
  FinanceLegacyProfit,
  FinanceLegacyOptions,
  FinanceLegacyUser,
} from '@services/finance-legacy.service.ts';
import { toastService } from '@services/toast.service.ts';

// Вікно створення/редагування "Профіт" (2026-08-21) - по аналогії з
// FinanceLegacyEntryFormDialog.tsx, але без project/emp/type (profit не має
// цих полів, не income/expense-типізований). Редагування (profit заданий) -
// лише для власних (sourceApp=WEB) записів, бек кине 403 на легасі.
export const FinanceLegacyProfitFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  options: FinanceLegacyOptions;
  profit?: FinanceLegacyProfit;
  onSaved: () => void;
}> = ({ open, onClose, options, profit, onSaved }) => {
  const [users, setUsers] = useState<FinanceLegacyUser[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [amount, setAmount] = useState('');
  const [createdUserId, setCreatedUserId] = useState('');
  const [createdAt, setCreatedAt] = useState<Date | null>(new Date());
  const [saving, setSaving] = useState(false);

  const isEdit = !!profit;

  useEffect(() => {
    if (!open) return;
    financeLegacyService.getUsers().then(setUsers).catch(() => toastService.error('Не вдалося завантажити юзерів.'));
  }, [open]);

  // Дефолти лише при СТВОРЕННІ - валюта "UAH" (за назвою з options, не
  // хардкод id), юзер - автопідстановка за email (той самий принцип, що й у
  // FinanceLegacyEntryFormDialog.tsx). Джерело - без дефолту, profit не має
  // еквівалента "Дима" (той дефолт специфічний для entries-витрат).
  const defaultsAppliedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      defaultsAppliedRef.current = false;
      return;
    }

    if (profit) {
      setSourceId(profit.sourceId ? String(profit.sourceId) : '');
      setCurrencyId(profit.currencyId ? String(profit.currencyId) : '');
      setAmount(profit.amount != null ? String(profit.amount) : '');
      setCreatedUserId(profit.createdUserId ? String(profit.createdUserId) : '');
      setCreatedAt(profit.createdAt ? new Date(profit.createdAt) : new Date());
      return;
    }

    setSourceId('');
    setCurrencyId('');
    setAmount('');
    setCreatedUserId('');
    setCreatedAt(new Date());
  }, [open, profit]);

  useEffect(() => {
    if (!open || profit || defaultsAppliedRef.current) return;
    if (options.currencies.length === 0) return;

    setCurrencyId(String(options.currencies.find((c) => c.label === 'UAH')?.value ?? ''));

    financeLegacyService.getMyLegacyUserMapping()
      .then(({ legacyUserId }) => { if (legacyUserId) setCreatedUserId(String(legacyUserId)); })
      .catch(() => {});

    defaultsAppliedRef.current = true;
  }, [open, profit, options]);

  const valid = sourceId && currencyId && amount && Number(amount) > 0 && createdUserId;

  const handleSave = async () => {
    if (!valid) {
      toastService.error('Заповніть обовʼязкові поля: джерело, валюта, сума, юзер.');
      return;
    }
    setSaving(true);
    try {
      const createdAtIso = createdAt ? createdAt.toISOString() : undefined;

      if (isEdit) {
        await financeLegacyService.updateProfit(profit!.id, {
          sourceId: Number(sourceId),
          currencyId: Number(currencyId),
          amount: Number(amount),
          createdUserId: Number(createdUserId),
          createdAt: createdAtIso,
        });
        toastService.success('Запис оновлено.');
      } else {
        await financeLegacyService.createProfit({
          sourceId: Number(sourceId),
          currencyId: Number(currencyId),
          amount: Number(amount),
          createdUserId: Number(createdUserId),
          createdAt: createdAtIso,
        });
        toastService.success('Запис створено.');
      }
      onSaved();
      onClose();
    } catch {
      toastService.error(isEdit ? 'Не вдалося оновити запис.' : 'Не вдалося створити запис.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редагувати виплату прибутку' : 'Додати виплату прибутку'}
      actions={<Button variant="contained" onClick={handleSave} disabled={saving || !valid}>{isEdit ? 'Зберегти' : 'Створити'}</Button>}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField select required label="Джерело" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
          {options.sources.map((s) => <MenuItem key={s.value} value={String(s.value)}>{s.label}</MenuItem>)}
        </TextField>

        <TextField select required label="Валюта" value={currencyId} onChange={(e) => setCurrencyId(e.target.value)}>
          {options.currencies.map((c) => <MenuItem key={c.value} value={String(c.value)}>{c.label}</MenuItem>)}
        </TextField>

        <TextField required type="number" label="Сума" value={amount} onChange={(e) => setAmount(e.target.value)} />

        <TextField
          select
          required
          label="Юзер (від імені якого пишеться запис)"
          value={createdUserId}
          onChange={(e) => setCreatedUserId(e.target.value)}
        >
          {users.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
          <DatePicker label="Дата" value={createdAt} onChange={setCreatedAt} />
        </LocalizationProvider>
      </Box>
    </BaseDialog>
  );
};
